"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import AdminColegioLayout from "../components/admincolegiolayout";

import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin, { Draggable } from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";

import { useAuth } from "../../../contexts/AuthContext";
import { adminColegioCalendarioService } from "../../../services/admin-colegio/calendario.service";

import type { EventoCalendarioDto } from "./dto/calendarios.dto";

type TipoActividadUI = {
  idTipoActividadCalendario: number;
  nombre: string;
  colorHex: string;
};

type PendingMove = {
  idEvento: number;
  fechaInicio: string;
  fechaFin: string | null;
};

type ToastType = "success" | "error" | "info";

type ToastState = {
  show: boolean;
  type: ToastType;
  message: string;
};

const toastStyles: Record<ToastType, string> = {
  success: "bg-green-600",
  error: "bg-red-600",
  info: "bg-gray-900",
};


export default function AdminColegioCalendarioPage() {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);

  const [tiposActividad, setTiposActividad] = useState<TipoActividadUI[]>([]);
  const [eventos, setEventos] = useState<EventoCalendarioDto[]>([]);

  const [mesActual, setMesActual] = useState<number>(new Date().getMonth() + 1);
  const [anioActual, setAnioActual] = useState<number>(
    new Date().getFullYear()
  );

  const [showCrearActividadModal, setShowCrearActividadModal] = useState(false);
  const [nombreNuevaActividad, setNombreNuevaActividad] = useState("");
  const [colorNuevaActividad, setColorNuevaActividad] = useState("#2563eb");

  const [showEliminarModal, setShowEliminarModal] = useState(false);
  const [eventoAEliminar, setEventoAEliminar] = useState<{
    idEvento: number;
    titulo: string;
  } | null>(null);

  const [pendingMoves, setPendingMoves] = useState<Record<number, PendingMove>>(
    {}
  );

  const [toast, setToast] = useState<ToastState>({
    show: false,
    type: "info",
    message: "",
  });

  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = (type: ToastType, message: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);

    setToast({ show: true, type, message });

    toastTimeoutRef.current = setTimeout(() => {
      setToast((prev) => ({ ...prev, show: false }));
    }, 2500);
  };

  const originalEventosRef = useRef<EventoCalendarioDto[]>([]);

  const calendarRef = useRef<FullCalendar | null>(null);

  const contenedorDraggableRef = useRef<HTMLDivElement | null>(null);
  const draggableInstanceRef = useRef<Draggable | null>(null);

  const autoSaveTimerRef = useRef<number | null>(null);
  const isSavingRef = useRef(false);

  const loadTiposActividad = async () => {
    const data = await adminColegioCalendarioService.getTiposActividad();

    const adaptado: TipoActividadUI[] = data
      .filter((t) => t.vigente)
      .map((t) => ({
        idTipoActividadCalendario: t.idTipoActividadCalendario,
        nombre: t.nombre,
        colorHex: t.colorHex,
      }));

    setTiposActividad(adaptado);
  };

  const loadEventos = async (mes: number, anio: number) => {
    const data = await adminColegioCalendarioService.getEventos(mes, anio);
    const vigentes = data.filter((e) => e.vigente);

    setEventos(vigentes);
    originalEventosRef.current = vigentes;
    setPendingMoves({});
  };

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        await loadTiposActividad();
        await loadEventos(mesActual, anioActual);
      } catch (error) {
        console.log("Error inicial calendario:", error);
      } finally {
        setLoading(false);
      }
    };

    if (user) init();
  }, [user, mesActual, anioActual]);

  useEffect(() => {
    if (!contenedorDraggableRef.current) return;

    if (loading) return;

    draggableInstanceRef.current?.destroy();

    draggableInstanceRef.current = new Draggable(contenedorDraggableRef.current, {
      itemSelector: ".fc-draggable-item",
      eventData: function (eventEl) {
        const title = eventEl.getAttribute("data-title") || "Actividad";
        const colorHex = eventEl.getAttribute("data-color") || "#2563eb";
        const tipoIdStr = eventEl.getAttribute("data-tipoid") || "";
        const tipoId = tipoIdStr ? Number(tipoIdStr) : null;

        return {
          title,
          backgroundColor: colorHex,
          borderColor: colorHex,
          textColor: "#111827",
          extendedProps: {
            idTipoActividadCalendario: tipoId,
            colorHex,
          },
        };
      },
    });

    return () => {
      draggableInstanceRef.current?.destroy();
      draggableInstanceRef.current = null;
    };
  }, [tiposActividad, loading]);


  const eventosFullCalendar = useMemo(() => {
    return eventos.map((e) => ({
      id: e.idEvento.toString(),
      title: e.titulo,
      start: e.fechaInicio,
      end: e.fechaFin ?? undefined,
      backgroundColor: e.colorHex ?? "#ffffff",
      borderColor: e.colorHex ?? "#e5e7eb",
      textColor: "#111827",
      extendedProps: {
        idEvento: e.idEvento,
        idTipoActividadCalendario: e.idTipoActividadCalendario,
        colorHex: e.colorHex,
        cursoId: e.cursoId ?? null,
        descripcion: e.descripcion ?? null,
      },
    }));
  }, [eventos]);

  const onCrearTipoActividad = async () => {
    if (!nombreNuevaActividad.trim()) return;

    try {
      const creado = await adminColegioCalendarioService.createTipoActividad({
        nombre: nombreNuevaActividad.trim(),
        colorHex: colorNuevaActividad,
      });

      setTiposActividad((prev) => [
        ...prev,
        {
          idTipoActividadCalendario: creado.idTipoActividadCalendario,
          nombre: creado.nombre,
          colorHex: creado.colorHex,
        },
      ]);

      setShowCrearActividadModal(false);
      setNombreNuevaActividad("");
      setColorNuevaActividad("#2563eb");
    } catch (error: any) {
      showToast("error", error?.response?.data?.message || "Error creando tipo de actividad");
    }
  };

  const autoGuardarSiEsUnicoMovimiento = (move: PendingMove) => {
    const cambiosActuales = Object.keys(pendingMoves).length;

    if (cambiosActuales >= 1) return;

    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    autoSaveTimerRef.current = window.setTimeout(async () => {
      const count = Object.keys(pendingMoves).length;
      if (count >= 1) return;

      if (isSavingRef.current) return;

      try {
        isSavingRef.current = true;
        setLoading(true);

        await adminColegioCalendarioService.updateEvento(move.idEvento, {
          fechaInicio: move.fechaInicio,
          fechaFin: move.fechaFin,
        });

        setEventos((prev) => {
          const nuevos = prev.map((e) =>
            e.idEvento === move.idEvento
              ? {
                ...e,
                fechaInicio: move.fechaInicio,
                fechaFin: move.fechaFin,
              }
              : e
          );
          originalEventosRef.current = nuevos;
          return nuevos;
        });
      } catch (error: any) {
        showToast("error", error?.response?.data?.message || "Error guardando movimiento");
        onCancelarCambios();
      } finally {
        setLoading(false);
        isSavingRef.current = false;
        autoSaveTimerRef.current = null;
      }
    }, 800);
  };

  const onEventDrop = (info: any) => {
    const idEvento = Number(info.event.id);

    const nuevaFechaInicio = info.event.startStr?.split("T")[0];
    const nuevaFechaFin = info.event.endStr
      ? info.event.endStr.split("T")[0]
      : null;

    const move: PendingMove = {
      idEvento,
      fechaInicio: nuevaFechaInicio,
      fechaFin: nuevaFechaFin,
    };

    setPendingMoves((prev) => {
      const next = {
        ...prev,
        [idEvento]: move,
      };

      const count = Object.keys(next).length;

      if (count === 1) {
        autoGuardarSiEsUnicoMovimiento(move);
      } else {
        if (autoSaveTimerRef.current) {
          window.clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
      }

      return next;
    });
  };

  const onGuardarCambios = async () => {
    const cambios = Object.values(pendingMoves);
    if (cambios.length < 2) return;

    try {
      setLoading(true);

      for (const c of cambios) {
        await adminColegioCalendarioService.updateEvento(c.idEvento, {
          fechaInicio: c.fechaInicio,
          fechaFin: c.fechaFin,
        });
      }

      originalEventosRef.current = eventos;
      setPendingMoves({});
      showToast("success", "Cambios guardados ✅");
    } catch (error: any) {
      showToast("error", error?.response?.data?.message || "Error guardando cambios");
      onCancelarCambios();
    } finally {
      setLoading(false);
    }
  };

  const onCancelarCambios = () => {
    if (autoSaveTimerRef.current) {
      window.clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }

    setEventos(originalEventosRef.current);
    setPendingMoves({});

    calendarRef.current?.getApi().refetchEvents();
  };

  const onEliminarEvento = async (info: any) => {
    const idEvento = Number(info.event.id);

    setEventoAEliminar({
      idEvento,
      titulo: info.event.title,
    });

    setShowEliminarModal(true);
  };

  const confirmarEliminarEvento = async () => {
    if (!eventoAEliminar) return;

    try {
      await adminColegioCalendarioService.deleteEvento(eventoAEliminar.idEvento);

      setEventos((prev) => {
        const nuevos = prev.filter((e) => e.idEvento !== eventoAEliminar.idEvento);
        originalEventosRef.current = nuevos;
        return nuevos;
      });

      setPendingMoves((prev) => {
        const copia = { ...prev };
        delete copia[eventoAEliminar.idEvento];
        return copia;
      });

      setShowEliminarModal(false);
      setEventoAEliminar(null);

      showToast("success", "Evento eliminado ✅");
    } catch (error: any) {
      alert(error?.response?.data?.message || "Error eliminando evento");
    }
  };

  const cantidadCambios = Object.keys(pendingMoves).length;

  return (
    <AdminColegioLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-6 text-white shadow">
            <h1 className="text-3xl font-bold">Calendario</h1>
            <p className="text-white/80 mt-1">
              Arrastra actividades al calendario para agendarlas.
            </p>
          </div>

          {cantidadCambios >= 2 && (
            <div className="bg-white border rounded-2xl shadow p-4 flex items-center justify-between">
              <div>
                <p className="font-bold text-gray-800">
                  Cambios pendientes: {cantidadCambios}
                </p>
                <p className="text-sm text-gray-500">
                  Mueve las actividades que quieras y al final guarda una sola vez.
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={onCancelarCambios}
                  className="border px-4 py-2 rounded-xl font-semibold"
                >
                  Cancelar
                </button>

                <button
                  onClick={onGuardarCambios}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-xl font-semibold"
                >
                  Guardar cambios ({cantidadCambios})
                </button>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-2xl shadow p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-bold text-lg">Actividades</h2>

                {user?.rol === "ADMINCOLEGIO" && (
                  <button
                    onClick={() => setShowCrearActividadModal(true)}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-xl text-sm font-semibold"
                  >
                    + Crear
                  </button>
                )}
              </div>

              <p className="text-sm text-gray-500 mb-4">
                Arrastra una actividad y suéltala en un día.
              </p>

              {loading ? (
                <p className="text-sm text-gray-500">Cargando...</p>
              ) : (
                <div
                  ref={contenedorDraggableRef}
                  className="grid grid-cols-2 gap-3"
                >
                  {tiposActividad.map((t) => (
                    <div
                      key={t.idTipoActividadCalendario}
                      data-title={t.nombre}
                      data-color={t.colorHex}
                      data-tipoid={t.idTipoActividadCalendario}
                      className="fc-draggable-item select-none cursor-grab active:cursor-grabbing
                               border rounded-xl p-3 text-center font-semibold text-sm
                               hover:bg-gray-50 transition"
                      style={{
                        borderColor: t.colorHex,
                      }}
                    >
                      {t.nombre}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow p-4 lg:col-span-3">
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                height="auto"
                locale="es"
                editable={true}
                droppable={true}
                eventStartEditable={true}
                eventDurationEditable={true}
                events={eventosFullCalendar}
                eventDrop={onEventDrop}
                eventClick={onEliminarEvento}
                eventReceive={async (info) => {
                  try {
                    const titulo = info.event.title;
                    const fechaInicio = info.event.startStr?.split("T")[0];

                    const tipoId = info.event.extendedProps?.idTipoActividadCalendario ?? null;
                    const colorHex = info.event.extendedProps?.colorHex ?? null;

                    if (!titulo || !fechaInicio) {
                      info.revert();
                      return;
                    }

                    const creado = await adminColegioCalendarioService.createEvento({
                      titulo,
                      fechaInicio,
                      fechaFin: null,
                      idTipoActividadCalendario: tipoId,
                      colorHex: colorHex,
                      cursoId: null,
                      descripcion: null,
                    });

                    info.event.remove();

                    setEventos((prev) => {
                      const nuevos = [...prev, creado];
                      originalEventosRef.current = nuevos;
                      return nuevos;
                    });
                  } catch (error: any) {
                    showToast("error", error?.response?.data?.message || "Error creando evento");
                    info.revert();
                  }
                }}
                datesSet={async (arg) => {
                  const start = arg.start;
                  const mes = start.getMonth() + 1;
                  const anio = start.getFullYear();

                  setMesActual(mes);
                  setAnioActual(anio);

                  try {
                    setLoading(true);
                    await loadEventos(mes, anio);
                  } catch (error) {
                    console.log("Error recargando eventos:", error);
                  } finally {
                    setLoading(false);
                  }
                }}
              />

            </div>
          </div>
        </div>

        {showCrearActividadModal && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
            onClick={() => setShowCrearActividadModal(false)}
          >
            <div
              className="bg-white rounded-2xl shadow-xl w-[420px] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold mb-1">Crear actividad</h2>
              <p className="text-sm text-gray-500 mb-4">
                Crea un tipo de actividad con nombre y color.
              </p>

              <label className="text-sm font-semibold">Nombre</label>
              <input
                className="border p-2 w-full rounded-xl mb-4 mt-1"
                placeholder="Ej: Examen, Prueba, Ensayo..."
                value={nombreNuevaActividad}
                onChange={(e) => setNombreNuevaActividad(e.target.value)}
              />

              <label className="text-sm font-semibold">Color</label>
              <input
                type="color"
                className="border p-2 w-full rounded-xl mb-4 mt-1 h-12"
                value={colorNuevaActividad}
                onChange={(e) => setColorNuevaActividad(e.target.value)}
              />

              <div className="flex justify-end gap-2">
                <button
                  className="border px-4 py-2 rounded-xl"
                  onClick={() => setShowCrearActividadModal(false)}
                >
                  Cancelar
                </button>

                <button
                  className={`px-4 py-2 rounded-xl text-white font-semibold ${!nombreNuevaActividad.trim()
                    ? "bg-purple-300 cursor-not-allowed"
                    : "bg-purple-600 hover:bg-purple-700"
                    }`}
                  disabled={!nombreNuevaActividad.trim()}
                  onClick={onCrearTipoActividad}
                >
                  Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-[9999]">
          <div
            className={`${toastStyles[toast.type]} text-white px-4 py-3 rounded-2xl shadow-lg flex items-center gap-3 min-w-[280px]`}
          >
            <span className="font-semibold">{toast.message}</span>
            <button
              className="ml-auto text-white/80 hover:text-white font-bold"
              onClick={() => setToast((prev) => ({ ...prev, show: false }))}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {showEliminarModal && eventoAEliminar && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => {
            setShowEliminarModal(false);
            setEventoAEliminar(null);
          }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-[420px] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-1 text-red-600">
              Eliminar actividad
            </h2>

            <p className="text-sm text-gray-500 mb-4">
              ¿Seguro que quieres eliminar{" "}
              <span className="font-semibold">"{eventoAEliminar.titulo}"</span>?
            </p>

            <div className="flex justify-end gap-2">
              <button
                className="border px-4 py-2 rounded-xl font-semibold"
                onClick={() => {
                  setShowEliminarModal(false);
                  setEventoAEliminar(null);
                }}
              >
                Cancelar
              </button>

              <button
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-xl font-semibold"
                onClick={confirmarEliminarEvento}
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}


    </AdminColegioLayout>
  );
}
